/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { animate } from '@lit-labs/motion';
import { consume } from '@lit/context';
import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';

import { StepEnum } from '@/api-sdk';
import { iconX } from '@/components/icons';
import { ConfiguratorContextType, configuratorGlobal } from '@/context';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, rootVariablesStyles } from '@/styles';

import '@/components/configurator/mobile/header/fg-mobile-header-main-image';
import '@/components/image/fg-image';

@customElement('fg-configurator-mobile-header')
export class FgConfiguratorMobileHeader extends LitElement {
  @property({ type: Object })
  selectedColor?: {
    image: string;
    color: string;
    selected: boolean;
    thumbImage: string;
  };

  @property({ type: Number })
  currentStep = 0;

  @property({ type: String })
  stepType?: StepEnum;

  @consume({ context: configuratorGlobal.context })
  configuratorContext!: ConfiguratorContextType;

  renderThumbHeaderImage(): string {
    const { productImage } = this.configuratorContext;

    if (!this.selectedColor) {
      return productImage || '';
    }
    const { thumbImage } = this.selectedColor;

    if (!thumbImage) {
      return productImage || '';
    }

    return thumbImage;
  }

  render() {
    return html`
      <header>
        <div class="mobile-header ">
          <span
            class=${!this.stepType?.includes('color') ? 'show' : 'hidden'}
            ${animate()}
          >
            <fg-image
              height="64"
              src=${live(this.renderThumbHeaderImage())}
              width="64"
            ></fg-image>
          </span>

          <h2>${this.configuratorContext.productName}</h2>
          <button
            @click=${() =>
              this.dispatchEvent(
                new CustomEvent('toggle-configurator', { composed: true }),
              )}
          >
            ${iconX}
          </button>
        </div>

        <fg-mobile-header-main-image
          .selectedColor=${this.selectedColor}
          .currentStep=${this.currentStep}
          .colorStep=${this.stepType?.includes('color')}
        ></fg-mobile-header-main-image>
      </header>
    `;
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    css`
      header {
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
        z-index: 1;
      }

      :host([part='wholesale']) header {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-bottom: 1px solid
          var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .mobile-header {
        display: flex;
        align-items: center;
        padding: 0 16px;
      }

      :host([part='wholesale']) .mobile-header {
        padding: 8px 16px;
      }

      .mobile-header__with-thumb {
        padding-left: 0;
        padding-right: 16px;
      }

      span {
        display: block;
        opacity: 0;
        width: 0;
        visibility: hidden;
      }

      span.show {
        opacity: 1;
        visibility: visible;
        width: 50px;
      }

      :host([part='wholesale']) span.show {
        width: 64px;
        margin-right: 8px;
      }

      span fg-image::part(image) {
        display: block;
        object-fit: contain;
        aspect-ratio: 1 / 1;
        width: 50px;
      }

      :host([part='wholesale']) span fg-image::part(image) {
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        width: 64px;
      }

      h2 {
        padding: 8px 0;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        margin-left: 8px;
      }

      :host([part='wholesale']) h2 {
        font-size: 1rem;
        text-overflow: unset;
        overflow: unset;
        white-space: unset;
      }

      .mobile-header button {
        margin-left: auto;
        color: var(--fg-primary, var(--fg-fallback-primary));
      }

      :host([part='wholesale']) .mobile-header button {
        color: var(--fg-secondary, var(--fg-fallback--secondary));
        margin-block: 4px auto;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        header {
          display: none;
        }
      }
    `,
  ];
}
